import { useTheme } from '@mui/material'
import React, { CSSProperties } from 'react'
import { toBoldStrings, useTranslation } from '../../../../i18n/useTranslation'
import { Icons } from '../../../../assets/icons'
import { isMobileAll, isMobilePortrait } from '../../../../utils'
import { useHistogramWidths } from './useHistogramWidths'
import { indices9to7 } from './helper'
import { BAR_BORDER_RADIUS, HBar } from './HBar'

const SHADOW_OVERLAP = 2
const BAR_GAP = 12
const TITLE_HEIGHT = 20
const TITLE_MARGIN_TOP = 4
const CONTAINER_HEIGHT = '15dvh'

export type HistogramBarsProps = {
    maxItemsPerBar: number[]
    itemsPerBar: number[]
    height?: number | string
    className?: string
    selectedBar?: number | null
    secondarySelectedBars?: number[]
    exceedingBars?: number[]
    barTitles?: string[]
    onSelectBar?: (index: number) => void
    highlightedBars?: number[]
    boxedBars?: number[]
    hasHintScale?: boolean
}

export const HistogramBars = ({
    maxItemsPerBar,
    itemsPerBar,
    height = CONTAINER_HEIGHT,
    className,
    selectedBar,
    secondarySelectedBars = [],
    exceedingBars = [],
    barTitles,
    onSelectBar,
    highlightedBars = [],
    boxedBars = [],
    hasHintScale = false,
}: HistogramBarsProps) => {
    const muiTheme = useTheme()
    const t = useTranslation()

    const DARK_COLOR = muiTheme.palette.primary.dark
    const NORMAL_COLOR = muiTheme.palette.primary.main
    const EMPTY_COLOR = muiTheme.palette.background.default
    const LIGHT_COLOR = muiTheme.palette.primary.light

    const maxAllBar = Math.max(...maxItemsPerBar)
    const hasTitles = barTitles && barTitles.length > 0
    const heightExpression = hasTitles
        ? `(${height} - ${TITLE_HEIGHT}px - ${TITLE_MARGIN_TOP}px)`
        : height
    const scaleFontSize = isMobileAll() ? 'xs' : 'lg'
    const totalBars = maxItemsPerBar.length //9
    const indices2keep = maxItemsPerBar
        .map((v, i) => (v > 0 ? i : null))
        .filter((v) => v !== null)
    const totalEffectiveBars = indices2keep.length
    const effectiveBoxedBars =
        totalEffectiveBars === 9 ? boxedBars : indices9to7(boxedBars)

    const { containerRef, barWidths, containerWidth } = useHistogramWidths(
        totalEffectiveBars,
        maxItemsPerBar
    )

    const renderBar = (i: number) => {
        const maxForCurrent = maxItemsPerBar[i]
        if (!maxForCurrent) {
            return null
        }

        const val = itemsPerBar[i] || 0
        const barHeight = `calc(${heightExpression} * ${
            maxForCurrent / maxAllBar
        })`
        const fillHeight = `${(val / maxForCurrent) * 100}%`
        const isEmpty = val === 0
        const isSelected = selectedBar === i
        const isSecondarySelected = secondarySelectedBars?.includes(i) || false
        const isExceeding = exceedingBars?.includes(i) || false
        const isHighlighted = highlightedBars?.includes(i) || false
        const fillColor = isSelected
            ? DARK_COLOR
            : isSecondarySelected
            ? NORMAL_COLOR
            : isEmpty
            ? EMPTY_COLOR
            : LIGHT_COLOR
        const title = hasTitles ? barTitles[i] || '' : ''

        return (
            <HBar
                key={i}
                index={i}
                maxForCurrent={maxForCurrent}
                val={val}
                barHeight={barHeight}
                fillHeight={fillHeight}
                isEmpty={isEmpty}
                isSelected={isSelected}
                isSecondarySelected={isSecondarySelected}
                isExceeding={isExceeding}
                isHighlighted={isHighlighted}
                fillColor={fillColor}
                title={title}
                hasTitles={hasTitles || false}
                onSelectBar={onSelectBar}
                dataBarIndex={i}
                containerHeight={String(height)}
            />
        )
    }

    const calculateBoxedBackground = () => {
        if (
            effectiveBoxedBars.length === 0 ||
            barWidths.length === 0 ||
            containerWidth === 0
        )
            return null

        const minIndex = Math.min(...effectiveBoxedBars)
        const maxIndex = Math.max(...effectiveBoxedBars)

        // console.debug('calculateBoxedBackground', {
        //     effectiveBoxedBars,
        //     minIndex,
        //     maxIndex,
        //     totalEffectiveBars,
        //     barWidths,
        //     containerWidth,
        // })

        // Calculate total width of bars and gaps
        const totalBarWidth = barWidths.reduce((sum, width) => sum + width, 0)
        const totalGapWidth = containerWidth - totalBarWidth
        const gapWidth =
            totalEffectiveBars > 1
                ? totalGapWidth / (totalEffectiveBars - 1)
                : 0

        // Calculate positions
        let leftPosition = 0
        let rightPosition = 0

        // Calculate left position (start of leftmost bar)
        for (let i = 0; i < minIndex; i++) {
            leftPosition += barWidths[i] + gapWidth
        }

        // Calculate right position (end of rightmost bar)
        for (let i = 0; i <= maxIndex; i++) {
            rightPosition += barWidths[i] + (i < maxIndex ? gapWidth : 0)
        }

        const leftPercent = (leftPosition / containerWidth) * 100
        const widthPercent =
            ((rightPosition - leftPosition) / containerWidth) * 100

        const effectiveShadowOverlap =
            effectiveBoxedBars.length === 1 ? SHADOW_OVERLAP : 0

        return {
            position: 'absolute',
            left: `${leftPercent - effectiveShadowOverlap}%`,
            // bottom: '-10%',
            width: `${widthPercent + effectiveShadowOverlap * 2}%`,
            // height: '120%',
            height: '100%',
            backgroundColor: LIGHT_COLOR,
            borderRadius: BAR_BORDER_RADIUS,
            opacity: 0.6,
            pointerEvents: 'none',
            zIndex: 0,
        } as CSSProperties
    }

    const boxedBgStyle = calculateBoxedBackground()

    return (
        <div
            className={`flex flex-col gap-1 ${height} ${className}`}
            style={{ direction: 'ltr' }} // arabic or not, keep this direction here
        >
            <div
                ref={containerRef}
                style={
                    {
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'space-between',
                        height,
                        gap: BAR_GAP,
                        background: 'transparent',
                        '--dark-color': DARK_COLOR,
                        '--empty-color': EMPTY_COLOR,
                    } as CSSProperties
                }
            >
                {boxedBgStyle && (
                    <div className={className} style={boxedBgStyle} />
                )}

                {Array.from({ length: totalBars })
                    .map((_, i) => renderBar(i))
                    .filter((c) => c !== null)}
            </div>

            {hasHintScale && (
                <div className="flex flex-row justify-between items-center">
                    <span
                        className={`text-${scaleFontSize} flex flex-row items-center gap-1`}
                    >
                        <Icons.ArrowBackL sx={{ fontSize: '1rem' }} />
                        {toBoldStrings(t('bottom_scale_hint')).map((s, i) => (
                            <span
                                className={`${i % 2 === 1 ? 'font-bold ' : ''}`}
                                key={i}
                            >
                                {s}
                            </span>
                        ))}
                    </span>
                    {!isMobilePortrait() && (
                        <span className={`text-${scaleFontSize}`}>
                            {t('moderately_important')}
                        </span>
                    )}
                    <span
                        className={`text-${scaleFontSize} flex flex-row items-center gap-1`}
                    >
                        {toBoldStrings(t('top_scale_hint')).map((s, i) => (
                            <span
                                className={`${i % 2 === 1 ? 'font-bold ' : ''}`}
                                key={i}
                            >
                                {s}
                            </span>
                        ))}
                        <Icons.ArrowForwardL sx={{ fontSize: '1rem' }} />
                    </span>
                </div>
            )}
        </div>
    )
}
